import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    organization: '',
    role: '',
    email: '',
    phone: '',
    productInterest: '',
    message: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const roleOptions = [
    { value: '', label: 'Select your role' },
    { value: 'surgeon', label: 'Surgeon' },
    { value: 'gastroenterologist', label: 'Gastroenterologist' },
    { value: 'endoscopist', label: 'Endoscopist' },
    { value: 'urologist', label: 'Urologist' },
    { value: 'procurement', label: 'Procurement Manager' },
    { value: 'administrator', label: 'Healthcare Administrator' },
    { value: 'technician', label: 'Medical Technician' },
    { value: 'other', label: 'Other' }
  ];

  const productInterestOptions = [
    { value: '', label: 'Select product category' },
    { value: 'endoscopes', label: 'Endoscopes & Imaging Systems' },
    { value: 'surgical-instruments', label: 'Surgical Instruments' },
    { value: 'spatz3', label: 'SPATZ3 Adjustable Intragastric Balloon' },
    { value: 'stents', label: 'Hanarostent® Esophagus Stents' },
    { value: 'polypectomy', label: 'Polypectomy Snares' },
    { value: 'peg-kit', label: 'PEG Kits & Components' },
    { value: 'accessories', label: 'Accessories & Consumables' },
    { value: 'training', label: 'Training & Education' },
    { value: 'service', label: 'Service & Maintenance' },
    { value: 'other', label: 'Other Products' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.name?.trim()) {
      newErrors.name = 'Full name is required';
    }

    if (!formData?.organization?.trim()) {
      newErrors.organization = 'Organization is required';
    }

    if (!formData?.role) {
      newErrors.role = 'Please select your role';
    }

    if (!formData?.email?.trim()) {
      newErrors.email = 'Email address is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData?.phone?.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^[\+]?[0-9\s\-\(\)]{8,}$/?.test(formData?.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }

    if (!formData?.productInterest) {
      newErrors.productInterest = 'Please select your product interest';
    }

    if (!formData?.message?.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData?.message?.trim()?.length < 10) {
      newErrors.message = 'Message must be at least 10 characters long';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    // Simulate form submission
    try {
      await new Promise(resolve => setTimeout(resolve, 2000));
      setIsSubmitted(true);
      setFormData({
        name: '',
        organization: '',
        role: '',
        email: '',
        phone: '',
        productInterest: '',
        message: ''
      });
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-success/5 border border-success/20 rounded-lg p-8 text-center"
      >
        <div className="flex items-center justify-center w-16 h-16 bg-success/10 rounded-full mx-auto mb-4">
          <Icon name="CheckCircle" size={32} color="var(--color-success)" />
        </div>
        <h3 className="text-xl font-semibold text-success mb-2">
          Message Sent Successfully!
        </h3>
        <p className="text-text-secondary mb-4">
          Thank you for contacting Endosurgery Malaysia. Our team will review your inquiry and respond within 24 hours during business days.
        </p>
        <p className="text-sm text-text-secondary mb-6">
          For urgent medical device support, please call our emergency hotline: +603-6157 8899
        </p>
        <Button
          variant="outline"
          onClick={() => setIsSubmitted(false)}
          iconName="RotateCcw"
          iconPosition="left"
        >
          Send Another Message
        </Button>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.6, delay: 0.2 }}
      className="bg-card border border-border rounded-lg p-6 shadow-medical"
    >
      <div className="mb-6">
        <h2 className="text-2xl font-semibold text-text-primary mb-2 flex items-center">
          <Icon name="MessageSquare" size={24} className="mr-3" />
          Get In Touch
        </h2>
        <p className="text-text-secondary">
          Send us a message and our medical device specialists will get back to you within 24 hours.
        </p>
      </div>
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Personal Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Full Name"
            type="text"
            placeholder="Enter your full name"
            value={formData?.name}
            onChange={(e) => handleInputChange('name', e?.target?.value)}
            error={errors?.name}
            required
          />
          <Input
            label="Organization"
            type="text"
            placeholder="Hospital/Clinic name"
            value={formData?.organization}
            onChange={(e) => handleInputChange('organization', e?.target?.value)}
            error={errors?.organization}
            required
          />
        </div>

        <Select
          label="Professional Role"
          options={roleOptions}
          value={formData?.role}
          onChange={(value) => handleInputChange('role', value)}
          error={errors?.role}
          required
          searchable
        />

        {/* Contact Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Email Address"
            type="email"
            placeholder="your.email@hospital.com"
            value={formData?.email}
            onChange={(e) => handleInputChange('email', e?.target?.value)}
            error={errors?.email}
            required
          />
          <Input
            label="Phone Number"
            type="tel"
            placeholder="+60 3-xxxx xxxx"
            value={formData?.phone}
            onChange={(e) => handleInputChange('phone', e?.target?.value)}
            error={errors?.phone}
            required
          />
        </div>

        <Select
          label="Product Interest"
          options={productInterestOptions}
          value={formData?.productInterest}
          onChange={(value) => handleInputChange('productInterest', value)}
          error={errors?.productInterest}
          required
          searchable
        />

        {/* Message */}
        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">
            Message <span className="text-error">*</span>
          </label>
          <textarea
            rows={6}
            placeholder="Please describe your inquiry, specific product requirements, or any questions you have about our medical devices..."
            value={formData?.message}
            onChange={(e) => handleInputChange('message', e?.target?.value)}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors duration-200 resize-none ${
              errors?.message ? 'border-error' : 'border-border'
            }`}
          />
          {errors?.message && (
            <p className="mt-1 text-sm text-error">{errors?.message}</p>
          )}
        </div>

        {/* Privacy Notice */}
        <div className="bg-muted rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <Icon name="Shield" size={20} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
            <div className="text-sm text-text-secondary">
              <p className="font-medium text-text-primary mb-1">Privacy & Data Protection</p>
              <p>
                Your information will be used solely to respond to your inquiry and provide relevant product information. 
                We comply with Malaysia's Personal Data Protection Act (PDPA) and never share your data with third parties.
              </p>
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <Button
          type="submit"
          variant="default"
          fullWidth
          loading={isSubmitting}
          iconName="Send"
          iconPosition="right"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Sending Message...' : 'Send Message'}
        </Button>
      </form>
    </motion.div>
  );
};

export default ContactForm;